/**
 * maintenanceUtils.js
 * Shared utility functions for maintenance-related operations
 */

/**
 * Convert maintenance type to short form label
 * 'ppm' → 'PPM'
 * 'calibration' → 'CAL'
 * 'compliance' → 'COM'
 * other → pass through unchanged
 *
 * @param {string} type - The maintenance type value
 * @returns {string} - The short form label
 */
export const getMaintenanceTypeLabel = (type) => {
  if (!type) return '';

  const typeStr = type.toString().toLowerCase();

  switch (typeStr) {
    case 'ppm':
      return 'PPM';
    case 'calibration':
      return 'CAL';
    case 'compliance':
      return 'COM';
    default:
      return type;
  }
};

/**
 * Get icon for maintenance type
 *
 * @param {string} type - The maintenance type value
 * @returns {string} - The emoji icon
 */
export const getMaintenanceTypeIcon = (type) => {
  if (!type) return '📅';

  const typeStr = type.toString().toLowerCase();

  switch (typeStr) {
    case 'ppm':
      return '🔧';
    case 'calibration':
      return '⚙️';
    case 'compliance':
      return '✅';
    default:
      return '📅';
  }
};

/**
 * Get CSS class name for maintenance type styling
 * Used with CSS classes like 'last-maintenance-type-badge', 'maintenance-type-badge', etc.
 *
 * @param {string} type - The maintenance type value
 * @returns {string} - The lowercase class name (ppm, calibration, compliance)
 */
export const getMaintenanceTypeClassName = (type) => {
  if (!type) return '';
  return type.toString().toLowerCase();
};

export const formatMaintenanceDate = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

export const formatMaintenanceDateTime = (dateString) => {
  if (!dateString) return { date: 'N/A', time: '' };
  const date = new Date(dateString);
  return {
    date: date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }),
    time: date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
  };
};

export const getMaintenanceTimeliness = (maintenanceDate, dueDate) => {
  if (!maintenanceDate || !dueDate) return { isOnTime: null, daysLate: 0 };

  const completed = new Date(maintenanceDate);
  const due = new Date(dueDate);
  const isOnTime = completed <= due;
  const diffMs = completed - due;
  const daysLate = isOnTime ? 0 : Math.ceil(diffMs / (1000 * 60 * 60 * 24));

  return { isOnTime, daysLate };
};
